// Conflict cards, ranked by severity across the whole weekend.
//
// Chronological order would list forty overlaps nobody will read. This list is the
// five decisions that matter, and the number beside each one says why it is where
// it is.

import { Link } from 'react-router-dom';
import { useApp } from '../../data/store.tsx';
import { fmtRange } from '../../engine/time.ts';
import '../../styles/planning-refinements.css';

export function Conflicts() {
  const { state, derived } = useApp();

  const all = [...derived.conflictsByDay.entries()]
    .flatMap(([dayId, list]) => list.map((c) => ({ dayId, conflict: c })))
    .sort((a, b) => b.conflict.severity - a.conflict.severity);

  if (state.members.length < 2) {
    return (
      <div className="panel planning-page planning-empty" style={{ maxWidth: 640 }}>
        <h1>Group choices</h1>
        <p className="muted">
          Add another planning profile to your crew to compare suggested schedules.
        </p>
        <div className="planning-actions"><Link to="/profile" className="buttonlink">Manage planning crew</Link>
          <Link to="/plan" className="buttonlink">Open my plan</Link></div>
      </div>
    );
  }

  if (all.length === 0) {
    return (
      <div className="panel planning-page planning-empty" style={{ maxWidth: 640 }}>
        <h1>Group choices</h1>
        <p className="muted">
          No group clashes were found in the current suggested schedules.
        </p>
        <Link to="/group" className="buttonlink">Compare group schedules</Link>
      </div>
    );
  }

  return (
    <div className="planning-page conflicts-page" style={{ maxWidth: 860 }}>
      <h1>Group choices</h1>
      <p className="lede">
        {all.length} {all.length === 1 ? 'clash' : 'clashes'} between suggested schedules, highest priority first.
        Compare options and vote on a plan together.
      </p>

      {all.map(({ dayId, conflict }) => {
        const day = state.days.find((d) => d.id === dayId);
        const rank = conflict.severity > 0.6 ? 'high' : conflict.severity > 0.3 ? 'medium' : 'low';
        const recommended = conflict.options.find((o) => o.id === conflict.recommendedOptionId);
        return (
          <div key={conflict.id} className={`card ${rank}`}>
            <header>
              <h3>
                {day?.label} {fmtRange(conflict.windowStart, conflict.windowEnd)}
              </h3>
              {conflict.lockCollision && <span className="badge warn">locks collide</span>}
              <span className="sev">{rank} priority</span>
            </header>

            <div className="positions">
              {conflict.memberPositions.map((p) => {
                const set = p.preferredSetId ? derived.setById.get(p.preferredSetId) : undefined;
                const stage = set ? derived.stageById.get(set.stageId) : undefined;
                return (
                  <span key={p.userId} className={`position ${p.band}`}>
                    <span className="dot" />
                    {p.displayName} wants {stage?.name ?? 'elsewhere'}
                    {p.rawScore !== undefined ? ` (${p.rawScore})` : ` (${p.band})`}
                    {p.hasHardLock && <span className="lock">locked</span>}
                  </span>
                );
              })}
            </div>

            {recommended && (
              <div className="option recommended">
                <span>
                  <span className="kind">{recommended.kind.replace(/_/g, ' ')}</span>
                  <br />
                  {recommended.label}
                </span>
              </div>
            )}
            <p className="reason">{conflict.recommendationReason}</p>
            <Link className="buttonlink" to={`/conflicts/${encodeURIComponent(conflict.id)}`}>Compare options</Link>
          </div>
        );
      })}
    </div>
  );
}
